import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import req from '@sportninja/common/api/request'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

/**
 * Constraint type from API
 */
export interface ConstraintType {
  id: string
  code: string
  name: string
  category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
}

/**
 * Scheduler constraint structure from API
 */
export interface SchedulerConstraint {
  id: string
  type: ConstraintType
  is_hard: boolean
  is_enabled: boolean
  priority: number
  weight: number
  parameters: Record<string, unknown>
  description: string | null
  division_uids: string[] | null
  team_uids: string[] | null
  sort_order: number
  created_at: string
  updated_at: string
}

/**
 * Blackout date display structure (transformed from constraint)
 */
export interface BlackoutDate {
  id: string
  constraintId: string
  date: string
  /** Raw YYYY-MM-DD date strings from the API */
  rawDates: string[]
  description: string
  division_uids: string[] | null
  venue_uids: string[] | null
}

/**
 * Bye week display structure (transformed from constraint)
 */
export interface ByeWeek {
  id: string
  constraintId: string
  period: string
  startDate: string
  endDate: string
}

/**
 * Form data for adding a blackout date
 */
export interface BlackoutDayFormData {
  dates: Dayjs[] | string[]
  description: string
  division_uids?: string[] | null
  venue_uids?: string[] | null
}

/**
 * Form data for adding a bye week
 */
export interface ByeWeekFormData {
  startDate: Dayjs | null
  endDate: Dayjs | null
  description: string
}

/**
 * Data for creating a new constraint
 */
export interface CreateConstraintData {
  constraint_type_code: string
  parameters: Record<string, unknown>
  description?: string
  is_hard?: boolean
  priority?: number
  weight?: number
  is_enabled?: boolean
  division_uids?: string[] | null
  team_uids?: string[] | null
}

/**
 * Partial-update payload used by per-row PUT and the `update` slot of
 * the bulk endpoint. Mirrors UpdateConstraintRequest's allowed fields.
 */
export interface UpdateConstraintData {
  parameters?: Record<string, unknown>
  description?: string
  is_hard?: boolean
  is_enabled?: boolean
  priority?: number
  weight?: number
  division_uids?: string[] | null
  team_uids?: string[] | null
  sort_order?: number
}

/**
 * Bulk-save operation set posted to /constraints/bulk. The endpoint
 * applies all three sections in a single transaction — partial failure
 * rolls everything back. Use this when saving an editor screen that
 * fans out into many constraint rows (e.g. the Matchup Config modal).
 */
export interface BulkConstraintOps {
  add?: CreateConstraintData[]
  update?: { id: string; data: UpdateConstraintData }[]
  delete?: string[]
}

export interface BulkConstraintsResult {
  added: SchedulerConstraint[]
  updated: SchedulerConstraint[]
  deleted_uids: string[]
}

/**
 * Data for reordering constraints
 */
export interface ReorderItem {
  id: string
  sort_order: number
}

/**
 * Hook result interface
 */
interface UseSchedulerConstraintsResult {
  /** Raw constraints from API */
  constraints: SchedulerConstraint[]
  /** Blackout dates (transformed from venue_blackout constraints) */
  blackoutDates: BlackoutDate[]
  /** Bye weeks (transformed from team_bye_period constraints) */
  byeWeeks: ByeWeek[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Refetch constraints (returns fresh data for direct use) */
  refetch: () => Promise<SchedulerConstraint[]>
  /** Add a blackout date constraint */
  addBlackoutDate: (data: BlackoutDayFormData) => Promise<SchedulerConstraint>
  /** Add a bye week constraint */
  addByeWeek: (data: ByeWeekFormData) => Promise<SchedulerConstraint>
  /** Add a constraint with full control */
  addConstraint: (data: CreateConstraintData) => Promise<SchedulerConstraint>
  /** Update a constraint */
  updateConstraint: (
    constraintId: string,
    data: Partial<{
      parameters: Record<string, unknown>
      description: string
      is_hard: boolean
      is_enabled: boolean
      priority: number
      weight: number
      division_uids: string[] | null
      team_uids: string[] | null
      sort_order: number
    }>
  ) => Promise<SchedulerConstraint>
  /** Delete a constraint */
  deleteConstraint: (constraintId: string) => Promise<void>
  /** Toggle constraint enabled state */
  toggleEnabled: (constraintId: string, enabled: boolean) => Promise<void>
  /** Update constraint type (hard/soft) */
  updateConstraintType: (constraintId: string, isHard: boolean) => Promise<void>
  /** Reorder constraints (bulk update sort_order) */
  reorderConstraints: (orderedItems: ReorderItem[]) => Promise<void>
  /**
   * Bulk add/update/delete in a single atomic call. Use this for
   * editor screens that produce many ops at once — partial failure
   * rolls back (no half-saved state).
   */
  bulkSaveConstraints: (
    ops: BulkConstraintOps
  ) => Promise<BulkConstraintsResult>
}

/**
 * Transform a venue_blackout constraint to a BlackoutDate display object
 * All dates are comma-separated in a single card
 */
const transformToBlackoutDate = (
  constraint: SchedulerConstraint
): BlackoutDate => {
  const params = constraint.parameters as {
    dates?: string[]
    venue_uids?: string[]
  }

  const dates = [...(params.dates || [])].sort()
  let dateDisplay = 'No dates'

  if (dates.length > 0) {
    dateDisplay = dates.map((d) => dayjs(d).format('MMM D, YYYY')).join(', ')
  }

  return {
    id: `blackout-${constraint.id}`,
    constraintId: constraint.id,
    date: dateDisplay,
    rawDates: dates,
    description: constraint.description || '',
    division_uids: constraint.division_uids,
    venue_uids: params.venue_uids || null,
  }
}

/**
 * Transform a team_bye_period constraint to a ByeWeek display object
 */
const transformToByeWeek = (
  constraint: SchedulerConstraint,
  index: number
): ByeWeek => {
  const params = constraint.parameters as {
    start_date?: string
    end_date?: string
  }

  const periodDisplay =
    params.start_date && params.end_date
      ? `${dayjs(params.start_date).format('MMM D, YYYY')} - ${dayjs(
          params.end_date
        ).format('MMM D, YYYY')}`
      : 'No period'

  return {
    id: `byeweek-${constraint.id}-${index}`,
    constraintId: constraint.id,
    period: periodDisplay,
    startDate: params.start_date || '',
    endDate: params.end_date || '',
  }
}

/**
 * Custom hook for managing scheduler constraints (blackout dates and bye weeks)
 *
 * @param flightId - The scheduler flight ID to manage constraints for
 * @returns Object containing constraints data, loading states, and CRUD functions
 */
export const useSchedulerConstraints = (
  flightId: string | null
): UseSchedulerConstraintsResult => {
  const [constraints, setConstraints] = useState<SchedulerConstraint[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const isOperatingRef = useRef(false)

  // Counter to ignore stale fetch responses when multiple fetches are in flight
  const fetchCounterRef = useRef(0)
  // Mutex chain to serialize mutations so concurrent toggles don't race
  const mutexRef = useRef<Promise<unknown>>(Promise.resolve())

  /**
   * Fetch all constraints for the flight.
   * Uses a counter to discard stale responses when multiple fetches overlap.
   */
  const fetchConstraints = useCallback(async () => {
    if (!flightId) {
      setConstraints([])
      return []
    }

    const thisCounter = ++fetchCounterRef.current
    setLoading(true)
    setError(null)

    try {
      const { data } = await req(`/scheduler/flights/${flightId}/constraints`)
      const result = data || []
      // Only apply state if this is still the latest fetch
      if (thisCounter === fetchCounterRef.current) {
        setConstraints(result)
      }
      return result
    } catch (err: unknown) {
      if (thisCounter === fetchCounterRef.current) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while fetching constraints')
        )
        setConstraints([])
      }
      return []
    } finally {
      if (thisCounter === fetchCounterRef.current) {
        setLoading(false)
      }
    }
  }, [flightId])

  /**
   * Add a blackout date constraint (venue_blackout).
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const addBlackoutDate = useCallback(
    (data: BlackoutDayFormData): Promise<SchedulerConstraint> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        if (!data.dates || data.dates.length === 0) {
          throw new Error('At least one date is required')
        }

        isOperatingRef.current = true
        try {
          // Convert dates to YYYY-MM-DD strings (accepts both Dayjs objects and strings)
          const dates = data.dates.map((d) =>
            typeof d === 'string' ? d : d.format('YYYY-MM-DD')
          )

          // Build the constraint payload
          const payload: CreateConstraintData = {
            constraint_type_code: 'venue_blackout',
            parameters: {
              dates,
            },
            division_uids:
              data.division_uids && data.division_uids.length > 0
                ? data.division_uids
                : null,
          }

          // Add venue UIDs to parameters if specified
          if (data.venue_uids && data.venue_uids.length > 0) {
            payload.parameters.venue_uids = data.venue_uids
          }

          if (data.description) {
            payload.description = data.description
          }

          const response = await req(
            `/scheduler/flights/${flightId}/constraints`,
            {
              method: 'POST',
              body: JSON.stringify(payload),
            }
          )

          // Refetch to update the list
          await fetchConstraints()

          return response.data
        } finally {
          isOperatingRef.current = false
        }
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Add a bye week constraint (team_bye_period).
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const addByeWeek = useCallback(
    (data: ByeWeekFormData): Promise<SchedulerConstraint> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        if (!data.startDate || !data.endDate) {
          throw new Error('Start date and end date are required')
        }

        isOperatingRef.current = true
        try {
          // Build the constraint payload
          const payload: CreateConstraintData = {
            constraint_type_code: 'team_bye_period',
            parameters: {
              start_date: data.startDate.format('YYYY-MM-DD'),
              end_date: data.endDate.format('YYYY-MM-DD'),
            },
          }

          if (data.description) {
            payload.description = data.description
          }

          const response = await req(
            `/scheduler/flights/${flightId}/constraints`,
            {
              method: 'POST',
              body: JSON.stringify(payload),
            }
          )

          // Refetch to update the list
          await fetchConstraints()

          return response.data
        } finally {
          isOperatingRef.current = false
        }
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Add a constraint with full control over all fields.
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const addConstraint = useCallback(
    (data: CreateConstraintData): Promise<SchedulerConstraint> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        isOperatingRef.current = true
        try {
          const response = await req(
            `/scheduler/flights/${flightId}/constraints`,
            {
              method: 'POST',
              body: JSON.stringify(data),
            }
          )

          // Refetch to update the list
          await fetchConstraints()

          return response.data
        } finally {
          isOperatingRef.current = false
        }
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Update a constraint.
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const updateConstraint = useCallback(
    (
      constraintId: string,
      data: Partial<{
        parameters: Record<string, unknown>
        description: string
        is_hard: boolean
        is_enabled: boolean
        priority: number
        weight: number
        division_uids: string[] | null
        team_uids: string[] | null
        sort_order: number
      }>
    ): Promise<SchedulerConstraint> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        const response = await req(
          `/scheduler/flights/${flightId}/constraints/${constraintId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          }
        )

        // Refetch to update the list
        await fetchConstraints()

        return response.data
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Delete a constraint.
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const deleteConstraint = useCallback(
    (constraintId: string): Promise<void> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        await req(
          `/scheduler/flights/${flightId}/constraints/${constraintId}`,
          {
            method: 'DELETE',
          }
        )

        // Refetch to update the list
        await fetchConstraints()
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Toggle constraint enabled state.
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const toggleEnabled = useCallback(
    (constraintId: string, enabled: boolean): Promise<void> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        await req(
          `/scheduler/flights/${flightId}/constraints/${constraintId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ is_enabled: enabled }),
          }
        )

        // Refetch to ensure server state is authoritative
        await fetchConstraints()
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Update constraint type (hard/soft).
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const updateConstraintType = useCallback(
    (constraintId: string, isHard: boolean): Promise<void> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        await req(
          `/scheduler/flights/${flightId}/constraints/${constraintId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ is_hard: isHard }),
          }
        )

        // Refetch to get updated sort order
        await fetchConstraints()
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Bulk add/update/delete constraints in a single atomic call.
   * Serialized via mutex like the other mutations.
   *
   * Used by editor screens (e.g. Matchup Config) that produce many
   * ops at once — replaces N concurrent POSTs with one transactional
   * write so partial failure rolls back.
   */
  const bulkSaveConstraints = useCallback(
    (ops: BulkConstraintOps): Promise<BulkConstraintsResult> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        isOperatingRef.current = true
        try {
          const body = {
            add: ops.add ?? [],
            update: (ops.update ?? []).map(({ id, data }) => ({
              id,
              ...data,
            })),
            delete: ops.delete ?? [],
          }
          const { data } = await req(
            `/scheduler/flights/${flightId}/constraints/bulk`,
            {
              method: 'POST',
              body: JSON.stringify(body),
            }
          )

          // Refresh the local cache exactly like the per-row APIs do.
          await fetchConstraints()

          return data as BulkConstraintsResult
        } finally {
          isOperatingRef.current = false
        }
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  /**
   * Reorder constraints (bulk update sort_order).
   * Serialized via mutex to prevent race conditions with concurrent operations.
   */
  const reorderConstraints = useCallback(
    (orderedItems: ReorderItem[]): Promise<void> => {
      const op = mutexRef.current.then(async () => {
        if (!flightId) {
          throw new Error('Flight ID is required')
        }

        await req(`/scheduler/flights/${flightId}/constraints/reorder`, {
          method: 'POST',
          body: JSON.stringify({ constraints: orderedItems }),
        })

        // Refetch to ensure server state is authoritative
        await fetchConstraints()
      })
      mutexRef.current = op.catch(() => {})
      return op
    },
    [flightId, fetchConstraints]
  )

  // Transform constraints to display formats
  const blackoutDates = useMemo(
    () =>
      constraints
        .filter((c) => c.type.code === 'venue_blackout')
        .map((c) => transformToBlackoutDate(c)),
    [constraints]
  )

  const byeWeeks = useMemo(
    () =>
      constraints
        .filter((c) => c.type.code === 'team_bye_period')
        .map((c, i) => transformToByeWeek(c, i)),
    [constraints]
  )

  // Fetch constraints when flightId changes (skip if a mutation is in progress)
  useEffect(() => {
    if (flightId) {
      if (!isOperatingRef.current) {
        fetchConstraints()
      }
    } else {
      setConstraints([])
    }
  }, [flightId]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    constraints,
    blackoutDates,
    byeWeeks,
    loading,
    error,
    refetch: fetchConstraints,
    addBlackoutDate,
    addByeWeek,
    addConstraint,
    updateConstraint,
    deleteConstraint,
    toggleEnabled,
    updateConstraintType,
    reorderConstraints,
    bulkSaveConstraints,
  }
}

export default useSchedulerConstraints
